import { useCallback } from 'react'
import { isEmpty } from '@/src/utils/helper'
import { travelDetailsSelection } from '@/src/store/slices/visaSlice/travelDetailsSlice/type'
import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { setVisaErrorFields } from '@/src/store/slices/visaSlice/visaErrorSlice'
import { getVisaRequirementsState } from '@/src/store/slices/visaSlice/visaRequirementsSlice'
import { getVisaErrorFields } from '@/src/store/slices/visaSlice/visaErrorSlice'

type ErrorState = {
    nationality: string
    destination: string
    visaType: string
    processingSpeed: string
    dateOfArrival: string
    paxDetails: string
}

const useVisaFormValidation = () => {
    const dispatch = useAppDispatch()
    const error = useAppSelector(getVisaErrorFields)
    const { visaType, processingSpeed, dateOfArrival, paxDetails } =
        useAppSelector(getVisaRequirementsState)

    // Clear errors for a specific field
    const clearError = useCallback((field: keyof ErrorState) => {
        dispatch(setVisaErrorFields({ [field]: '' }))
    }, [])

    // Validate travel details form
    const validateTravelDetails = useCallback(
        (
            nationality: travelDetailsSelection,
            destination: travelDetailsSelection
        ): boolean => {
            const { value: selectedNationality } = nationality
            const { value: selectedDestination } = destination

            if (
                !isEmpty(selectedNationality) &&
                !isEmpty(selectedDestination)
            ) {
                return true
            }

            const fieldErrors = {
                nationality: isEmpty(selectedNationality)
                    ? 'Nationality is required'
                    : '',
                destination: isEmpty(selectedDestination)
                    ? 'Destination is required'
                    : ''
            }

            dispatch(setVisaErrorFields(fieldErrors))
            return false
        },
        []
    )

    // Validate visa requirements form
    const validateVisaRequirements = useCallback((): boolean => {
        let isValid = true
        const { selectedOption } = visaType
        const { adults, children } = paxDetails
        const maxPaxCount = 10
        const fieldErrors = {
            visaType: '',
            processingSpeed: '',
            dateOfArrival: '',
            paxDetails: ''
        }
        if (isEmpty(selectedOption)) {
            fieldErrors.visaType = 'Please select a visa type'
            isValid = false
        }
        if (isEmpty(processingSpeed)) {
            fieldErrors.processingSpeed = 'Please select a processing speed'
            isValid = false
        }
        if (isEmpty(dateOfArrival)) {
            fieldErrors.dateOfArrival = 'Please select a date of arrival'
            isValid = false
        }
        if (adults + children > maxPaxCount) {
            fieldErrors.paxDetails = 'Maximum 10 pax allowed'
            isValid = false
        }

        dispatch(setVisaErrorFields(fieldErrors))
        return isValid
    }, [visaType, processingSpeed, dateOfArrival, paxDetails])

    return {
        error,
        clearError,
        validateTravelDetails,
        validateVisaRequirements
    }
}

export default useVisaFormValidation
